import React, { useEffect } from "react";

import "../pages/AdminSistema.css";

type Props = {
  isOpen: boolean;
  /** `deactivate` desativa; `reactivate` reativa. */
  action: "deactivate" | "reactivate";
  /** "usuário" ou "freelancer" — entra no texto da pergunta. */
  entityLabel: string;
  /** Nome de quem será afetado. */
  name: string;
  /**
   * Motivo pelo qual a ação é impossível. Quando presente, o modal explica e
   * não oferece o botão de confirmar — é o caso de desativar a si mesmo, que o
   * backend também recusa (403), mas que não deve parecer um erro do sistema.
   */
  blockedReason?: string | null;
  processing?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const ConfirmToggleActiveModal: React.FC<Props> = ({
  isOpen,
  action,
  entityLabel,
  name,
  blockedReason,
  processing = false,
  onConfirm,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !processing) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = anterior;
    };
  }, [isOpen, onClose, processing]);

  if (!isOpen) return null;

  const desativando = action === "deactivate";
  const titulo = desativando ? `Desativar ${entityLabel}` : `Reativar ${entityLabel}`;

  return (
    <div
      className="admModalOverlay"
      onClick={() => {
        if (!processing) onClose();
      }}
    >
      <div
        className="admModal"
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admModal__header">
          <h2 className="admModal__title">{titulo}</h2>
          <button
            type="button"
            className="admModal__close"
            onClick={onClose}
            disabled={processing}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <div className="admModal__body">
          {blockedReason ? (
            <p style={{ margin: 0, fontSize: 14, color: "#b91c1c" }}>{blockedReason}</p>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 14 }}>
                {desativando ? "Desativar" : "Reativar"} <strong>{name}</strong>?
              </p>
              <p className="admMuted" style={{ margin: 0 }}>
                {desativando
                  ? "O acesso ao sistema é bloqueado imediatamente. O cadastro não é apagado e pode ser reativado depois."
                  : "O cadastro volta a aparecer entre os ativos e o acesso ao sistema é liberado de novo."}
              </p>
            </>
          )}
        </div>

        <div className="admModal__footer">
          <button
            type="button"
            className="admBtn admBtn--secondary"
            onClick={onClose}
            disabled={processing}
          >
            {blockedReason ? "Fechar" : "Cancelar"}
          </button>
          {blockedReason ? null : (
            <button
              type="button"
              className={`admBtn ${desativando ? "admBtn--danger" : "admBtn--primary"}`}
              onClick={onConfirm}
              disabled={processing}
            >
              {processing ? "Processando..." : desativando ? "Desativar" : "Reativar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmToggleActiveModal;
